import React, { useState, useEffect } from "react";
import { Menu, X, Instagram, Phone, MessageCircle } from "lucide-react";

const LOGO_URL = "/aloma-logo.png";
const WHATSAPP_LINK =
  "https://wa.me/523223842690?text=" +
  encodeURIComponent(
    "Hola, me interesa cotizar un servicio con Alambrados Aloma. ¿Podrían compartirme más información?"
  );

const LINKS = [
  { id: "inicio", label: "Inicio" },
  { id: "nosotros", label: "Nosotros" },
  { id: "servicios", label: "Servicios" },
  { id: "proyectos", label: "Proyectos" },
  { id: "contacto", label: "Contacto" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Top contact bar */}
      <div
        data-testid="topbar"
        className="bg-[#005EB8] text-white border-b border-[#FFF44F]/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9 md:h-10 text-xs">
          <div className="flex items-center gap-2 md:gap-3">
            <span className="font-mono-tag text-[10px] text-[#FFF44F] hidden sm:inline">
              CONTACTO DIRECTO
            </span>
            <span className="text-[#FFF44F] sm:hidden">●</span>
          </div>
          <div className="flex items-center gap-4 md:gap-6">
            <a
              href="tel:+523223842690"
              data-testid="topbar-phone"
              className="flex items-center gap-2 hover:text-[#FFF44F] transition-colors"
            >
              <Phone size={13} />
              <span className="font-mono-tag text-[11px]">322 384 2690</span>
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-whatsapp"
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] px-3 py-1 transition-colors"
            >
              <MessageCircle size={13} />
              <span className="font-mono-tag text-[11px] hidden sm:inline">WHATSAPP</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div
        className={`border-b transition-all duration-300 ${
          scrolled
            ? "bg-[#F4F1E8]/95 backdrop-blur-md border-[#0A0A0A]"
            : "bg-[#F4F1E8]/85 backdrop-blur-sm border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="navbar-logo"
            onClick={() => scrollTo("inicio")}
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="Alambrados Aloma"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="font-mono-tag text-xs text-[#0A0A0A] hover:text-[#005EB8] link-underline transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a
              href="https://www.instagram.com/alambradosaloma?igsh=NzRsc3RzeXVqc2pp"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-instagram"
              className="text-[#0A0A0A] hover:text-[#005EB8] transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <button
              data-testid="navbar-cta"
              onClick={() => scrollTo("contacto")}
              className="bg-[#0A0A0A] text-[#FFF44F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#FFF44F] hover:text-[#0A0A0A] transition-colors border-2 border-[#0A0A0A]"
            >
              Cotizar
            </button>
          </nav>

          <button
            data-testid="navbar-mobile-toggle"
            className="md:hidden text-[#0A0A0A]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden border-t-2 border-[#0A0A0A] bg-[#F4F1E8]"
          data-testid="navbar-mobile-menu"
        >
          <div className="px-4 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="font-display text-2xl text-left text-[#0A0A0A] hover:text-[#005EB8] transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-navbar-cta"
              onClick={() => scrollTo("contacto")}
              className="mt-2 bg-[#0A0A0A] text-[#FFF44F] px-5 py-3 font-bold uppercase tracking-widest text-sm"
            >
              Cotizar Proyecto
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
